import { CircleCheck, Loader, Clock } from 'lucide-react';
import { i18n } from '@/shared/i18n';
import {
  useFocusGroupRunStore,
  type PersonaRunStatus,
  type ReportStatus,
} from '../../stores/focusGroupRunStore';
import { useTranslation } from 'react-i18next';

const StatusIcon = ({ status }: { status: PersonaRunStatus | ReportStatus }) => {
  switch (status) {
    case 'done':
      return <CircleCheck size={24} className="text-green-500 shrink-0" />;
    case 'running':
      return <Loader size={24} className="text-primary shrink-0 animate-spin" />;
    case 'pending':
    default:
      return <Clock size={24} className="text-muted-foreground shrink-0" />;
  }
};

const getPersonaDescription = (
  status: PersonaRunStatus,
  currentQuestion: number,
  totalQuestions: number,
): string => {
  switch (status) {
    case 'done':
      return i18n.t('audiences:focusGroup.runStep.done');
    case 'running':
      if (totalQuestions > 0) {
        return i18n.t('audiences:focusGroup.runStep.respondingToQuestion', {
          currentQuestion,
          totalQuestions,
        });
      }
      return i18n.t('audiences:focusGroup.runStep.analyzingProjectGoalsAndObjectives');
    case 'pending':
    default:
      return i18n.t('audiences:focusGroup.runStep.notStarted');
  }
};

const RunProgress = () => {
  const { t } = useTranslation('audiences');

  const personas = useFocusGroupRunStore((s) => s.personas);
  const reportStatus = useFocusGroupRunStore((s) => s.reportStatus);
  const error = useFocusGroupRunStore((s) => s.error);

  return (
    <div className="flex flex-col gap-12 items-center w-full max-w-[650px]">
      <h2 className="text-xl font-medium text-foreground tracking-tight text-center w-full">
        {t('focusGroup.runStep.runAndGenerateResults')}
      </h2>

      <div className="flex flex-col gap-2 w-full">
        {personas.map((persona) => (
          <div
            key={persona.personaId}
            className="flex items-center gap-4 p-4 rounded-lg border border-border"
          >
            <StatusIcon status={persona.status} />
            <div className="flex flex-col gap-0.5 min-w-0">
              <p className="text-base font-medium text-foreground truncate">
                {persona.personaName}
              </p>
              <p className="text-xs text-muted-foreground">
                {getPersonaDescription(
                  persona.status,
                  persona.currentQuestion,
                  persona.totalQuestions,
                )}
              </p>
            </div>
          </div>
        ))}

        <div className="flex items-center gap-4 p-4 rounded-lg border border-border">
          <StatusIcon status={reportStatus} />
          <div className="flex flex-col gap-0.5 min-w-0">
            <p className="text-base font-medium text-foreground">
              {t('focusGroup.runStep.report')}
            </p>
            <p className="text-xs text-muted-foreground">
              {reportStatus === 'done'
                ? t('focusGroup.runStep.done')
                : reportStatus === 'running'
                  ? t('focusGroup.runStep.generatingReport')
                  : t('focusGroup.runStep.notStarted')}
            </p>
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-lg border border-destructive bg-destructive/10">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const RunStep = () => {
  return <RunProgress />;
};
